import React, { useEffect, useMemo, useState } from 'react';
import { Badge, InputGroup, Form, Spinner } from 'react-bootstrap';
import { FaSearch, FaUsers } from 'react-icons/fa';
import api from '../../api/apiClient';
import { toast } from 'react-toastify';

const InstructorManageUsers = () => {
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    api.instructor.getUsers()
      .then((res) => {
        if (res.data?.success) setUsers(Array.isArray(res.data.data) ? res.data.data : []);
      })
      .catch((err) => toast.error('Failed to load users: ' + (err.response?.data?.message || err.message)))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase();
    if (!term) return users;
    return users.filter((u) =>
      (u.first_name || '').toLowerCase().includes(term) ||
      (u.last_name  || '').toLowerCase().includes(term) ||
      (u.email      || '').toLowerCase().includes(term)
    );
  }, [users, searchTerm]);

  return (
    <div className="p-3 sm:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
      <div className="mb-5">
        <h4 className="text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-red-600 mb-1">
          My Users
        </h4>
        <p className="text-sm text-gray-500">Learners enrolled in your assigned courses (read-only view).</p>
      </div>

      <div className="mb-4 max-w-sm">
        <InputGroup className="shadow-sm rounded-xl overflow-hidden">
          <InputGroup.Text className="bg-white border-0 text-purple-600 px-3"><FaSearch /></InputGroup.Text>
          <Form.Control
            type="search"
            placeholder="Search by name or email…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border-0 text-sm"
          />
        </InputGroup>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-24">
          <Spinner animation="border" variant="primary" style={{ width: 48, height: 48 }} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white/80 rounded-2xl shadow border border-purple-100">
          <FaUsers size={48} className="text-purple-300 mx-auto mb-3" />
          <p className="text-purple-700 font-medium">
            {searchTerm ? 'No users match your search.' : 'No learners enrolled in your assigned courses yet.'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl shadow-lg bg-white">
          <table className="w-full text-sm table table-bordered table-hover m-0">
            <thead className="bg-gradient-to-r from-purple-500 to-red-600 text-white">
              <tr>
                <th className="py-3 px-4 text-left font-bold">Name</th>
                <th className="py-3 px-4 text-left font-bold">Email</th>
                <th className="py-3 px-4 text-left font-bold">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.id} className="border-b border-purple-50 hover:bg-purple-50/40">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-400 to-red-500 flex items-center justify-center text-white font-bold text-sm shadow">
                        {((u.first_name?.[0] || '') + (u.last_name?.[0] || '')).toUpperCase() || 'U'}
                      </div>
                      <span className="font-semibold text-gray-800">{u.first_name} {u.last_name}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-gray-500 break-all">{u.email}</td>
                  <td className="py-3 px-4">
                    <Badge bg={u.status === 1 ? 'success' : 'secondary'} className="px-2 py-1 text-xs">
                      {u.status === 1 ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InstructorManageUsers;
